import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

// Pernod Ricard brand blue primary (matches .text-brand) with AA-compliant overrides.
// Light: #234E8C on white ≈ 8.4:1 — passes WCAG AAA
// Dark:  #7FB3D9 on surface ≈ 8.5:1 — passes WCAG AAA
const AuraBrand = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '#234E8C',
          contrastColor: '#ffffff',
          hoverColor: '#1a3c6b',
          activeColor: '#122a4d',
        },
        highlight: {
          background: 'color-mix(in srgb, #234E8C, transparent 92%)',
          focusBackground: 'color-mix(in srgb, #234E8C, transparent 84%)',
          color: '#234E8C',
          focusColor: '#1a3c6b',
        },
        // Form-input placeholder + disabled text bumped to AA (≥4.5:1) on the
        // disabled background {surface.200} (#E2E8F0). {surface.600} ≈ 5.7:1.
        // Aura's token key is camelCase `placeholderColor`, NOT nested.
        formField: {
          placeholderColor: '{surface.600}',
          disabledColor: '{surface.600}',
        },
      },
      dark: {
        primary: {
          color: '#7FB3D9',
          contrastColor: '#0a1a33',
          hoverColor: '#a3c8e3',
          activeColor: '#c7dcee',
        },
        highlight: {
          background: 'color-mix(in srgb, #7FB3D9, transparent 84%)',
          focusBackground: 'color-mix(in srgb, #7FB3D9, transparent 76%)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
        // Form-input placeholder + disabled text bumped to AA (≥4.5:1) on the
        // disabled background {surface.700} (#334155). {surface.300} ≈ 6.8:1.
        formField: {
          placeholderColor: '{surface.300}',
          disabledColor: '{surface.300}',
        },
      },
    },
  },
})
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import 'primeicons/primeicons.css'
import 'primeflex/primeflex.css'
import './assets/styles/layout.css'

import App from './App.vue'
import router from './router'
import { useAuthStore } from './stores/auth'

async function bootstrap() {
  const app = createApp(App)
  const pinia = createPinia()

  app.use(pinia)
  app.use(PrimeVue, {
    theme: {
      preset: AuraBrand,
      options: {
        darkModeSelector: '.p-dark',
        cssLayer: false,
      },
    },
    ripple: true,
    pt: {
      treeselect: {
        panel: {
          style: 'min-width: 22rem; max-width: 22rem;',
        },
        treeContainer: {
          style: 'max-height: 20rem;',
        },
      },
    },
  })
  app.use(ConfirmationService)
  app.use(ToastService)
  app.directive('tooltip', Tooltip)

  // Initialize MSAL before mounting — handles redirect callback
  const authStore = useAuthStore()
  await authStore.initialize()

  app.use(router)
  app.mount('#app')
}

bootstrap()
